<template>
<vis-network ref="network" :nodes="nodes" :edges="edges" :options="options" @nodes-add="fit()"></vis-network>
</template>

<script>
import UserAuth from '@/components/auth/UserAuth'
import {
  Network
} from 'vue2vis';
import colors from 'vuetify/es5/util/colors'

const edgeColors = [
  colors.red.darken1,
  colors.teal.darken1,
  colors.pink.darken1,
  colors.cyan.darken1,
  colors.purple.darken1,
  colors.green.darken1,
  colors.deepPurple.darken1,
  colors.lime.darken1,
  colors.indigo.darken1,
  colors.yellow.darken1,
  colors.blue.darken1,
  colors.orange.darken1,
  colors.lightBlue.darken1,
  colors.amber.darken1,
  colors.lightGreen.darken1,
  colors.deepOrange.darken1,
  colors.brown.darken1
];

const NOT_AVAILABLE = -129;
const MAX_POWER_SATURATED = -130;
const NO_SIGNAL = -131;

export default {
  name: 'RouteAnalysisChart',
  mixins: [UserAuth],
  props: ['mac', 'eventlog', 'optimize', 'level'],
  components: {
    'vis-network': Network
  },
  data() {
    return {
      options: {
        autoResize: true,
        nodes: {
          shape: 'circle',
          color: colors.blue.base,
          font: {
            color: '#fff'
          }
        },
        edges: {
          arrows: 'to'
        },
        layout: {},
        physics: {
          solver: 'repulsion',
          repulsion: {
            nodeDistance: 150,
            springLength: 200,
            springConstant: 0.1
          }
        },
        interaction: {
          selectConnectedEdges: false
        }
      },
      nodeGateway: {
        id: 'gateway',
        label: this.$t('route.gateway'),
        fixed: true,
        color: colors.green.lighten1,
        shape: 'ellipse',
        x: 0,
        y: 0
      },
      timeToIgnore: 0
    }
  },
  computed: {
    mergedDevice() {
      let gateway = this.userDevice[this.mac] || {};
      return Object.values(gateway).filter(d => d.uid != 510).map(d => {
        let channels = Object.values((d || {}).channel || {});
        return {
          uid: d.uid,
          name: channels.map(c => `${(((this.userTargetName||{})[this.mac]||{})[d.uid]||{})[c.cid]||''} (${c.name}) [${c.functype}]`).join('<br>'),
          functype: (channels.find(c => c.functype) || {}).functype,
          channels: channels
        };
      }).filter(d => d.functype);
    },
    nodes() {
      return [
        this.nodeGateway,
        ...this.mergedDevice.map(d => ({
          id: d.uid,
          title: `UID: ${d.uid}<br>${d.name}`,
          image: this.$FuncTypeIcons[d.functype] || this.$FuncTypeIcons['unknown'],
          shape: 'circularImage',
          color: colors.blue.lighten5,
          size: 16,
          label: `${d.uid}`,
          font: {
            color: '#000',
            strokeWidth: 2
          }
        }))
      ];
    },
    edges() {
      let routes = this.optimize ? this.routesOptimized : this.routesRaw;
      let edges = [];
      Object.keys(routes).forEach(uid => {
        let s = routes[uid].speed;
        let r = routes[uid].nodes;
        r.reduce((acc, cur) => {
          edges.push({
            id: `${cur.uid}-${acc.uid}-${uid}`,
            label: `${this.labelEdge(cur.rssi)}`,
            from: acc.uid,
            to: cur.uid,
            color: {
              color: edgeColors[uid % edgeColors.length],
              inherit: false
            },
            width: s + 1,
            title: `${this.$t('route.target')}: ${uid}<br>${this.$t('route.speed')}: ${['9600','40K','100K'][s-1]}`,
            rssi: cur.rssi
          });
          return cur;
        }, {
          uid: 'gateway',
          rssi: 0
        });
      });
      return edges;
    },
    routesRaw() {
      let r = {}
      let gateway = this.userDevice[this.mac] || {};
      Object.values(gateway).map(d => {
        Object.keys(d.channel || {}).forEach(c => {
          let ev = this.eventlog[c] || {};
          let tx = ev.tx_status;
          if (tx && tx.nodes && tx.nodes.length >= this.level && ev.timestamp * 1000 > this.timeToIgnore) {
            r[d.uid] = {
              nodes: tx.nodes,
              speed: tx.routespeed
            };
          }
        })
      });
      return r;
    },
    routesMap() {
      let edge = {};
      for (let rs of Object.values(this.routesRaw)) {
        let from = 'gateway';
        for (let r of rs.nodes) {
          let to = r.uid;
          if (!edge[to]) edge[to] = {};
          if (!edge[from]) edge[from] = {};
          if (!edge[to][from]) edge[to][from] = {};
          if (!edge[from][to]) edge[from][to] = {};
          if ((r.rssi > -100 || r.rssi == MAX_POWER_SATURATED || r.rssi == NOT_AVAILABLE) && (edge[to][from].rssi || -256) < r.rssi) {
            edge[to][from].rssi = r.rssi;
            edge[from][to].rssi = r.rssi;
            edge[to][from].speed = rs.speed;
            edge[from][to].speed = rs.speed;
          }
          from = to;
        }
      }
      return edge;
    },
    routesOptimized() {
      let map = this.routesMap;
      let expandRoute = function(node, path) {
        path.push(node);
        if (node == 'gateway') return [node];
        if (path.length > 5) return;
        let nextNodes = Object.keys(map[node]);
        if (nextNodes) {
          return nextNodes.filter(n => path.indexOf(n) == -1).map(n => {
            let p = expandRoute(n, path.slice());
            if (p) p.push(node);
            return p;
          }).sort((a, b) => a.length - b.length).find(n => n);
        }
      };
      let routes = {};
      Object.keys(map).filter(c => c != 'gateway').forEach(c => routes[c] = expandRoute(c, []));
      for (let uid in routes) {
        let r = routes[uid];
        let speed = 0;
        let nodes = [];
        r.reduce((from, to) => {
          if (from) {
            nodes.push({
              uid: parseInt(to),
              rssi: map[from][to].rssi
            });
            speed = Math.max(speed, map[from][to].speed);
          }
          return to;
        });
        routes[uid] = {
          nodes: nodes,
          speed: speed
        };
      }
      return routes;
    },
    routesDiff() {
      let raw = this.routesRaw;
      let opt = this.routesOptimized;
      let diff = {};
      Object.keys(raw).forEach(r => {
        let uidsRaw = raw[r].nodes.map(n => n.uid);
        let uidsOpt = opt[r].nodes.map(n => n.uid);
        if (uidsRaw.length == uidsOpt.length && uidsRaw.reduce((acc, cur, i) => (acc && cur != uidsOpt[i]) ? false : acc, true)) return;
        diff[r] = {
          before: raw[r],
          after: opt[r]
        };
      });
      return diff;
    }
  },
  methods: {
    fit() {
      this.$refs.network.fit({
        animation: true
      });
    },
    labelEdge(rssi) {
      switch (rssi) {
        case NOT_AVAILABLE:
          return this.$t('route.not_available');
        case MAX_POWER_SATURATED:
          return this.$t('route.max_power_saturated');
        case NO_SIGNAL:
          return this.$t('route.no_signal');
        default:
          return `${rssi}dBm`;
      }
    }
  }
}
</script>

<style>
@import 'vue2vis/dist/vue2vis.css';
</style>



// WEBPACK FOOTER //
// src/components/juice/RouteAnalysisChart.vue